import React, {useEffect, useState, useCallback} from 'react'
import {BellIcon, CheckCircleIcon} from 'lucide-react'
import toaster from '../../../Utils/toaster'

interface NotificationBoxProps {
  userType?: string
}

export const NotificationBox: React.FC<NotificationBoxProps> = ({userType}) => {
  const [showNotificationBox, setShowNotificationBox] = useState(false)
  const [notificationPermissionGranted, setNotificationPermissionGranted] = useState(false)
  const [showReloadMessage, setShowReloadMessage] = useState(false)

  // Function to check and update notification permission status
  const checkNotificationPermission = useCallback(() => {
    const isInterpreter = userType === 'INTERPRETER'
    
    if (!isInterpreter) {
      setShowNotificationBox(false)
      return
    }

    // Always check current notification permission status first
    if (typeof window !== 'undefined' && typeof window.Notification !== 'undefined') {
      const permission = window.Notification.permission
      const wasAlreadyGranted = localStorage.getItem('notificationPermissionGranted') === 'true'
      
      // If permission was already granted and user reloaded, hide the box immediately
      if (permission === 'granted' && wasAlreadyGranted) {
        setShowNotificationBox(false)
        setNotificationPermissionGranted(true)
        setShowReloadMessage(false)
        return
      }

      // If reload message is showing, don't override it (user just granted permission)
      if (showReloadMessage) {
        return
      }
      
      if (permission === 'default') {
        // Permission not yet requested
        setShowNotificationBox(true)
        setNotificationPermissionGranted(false)
        setShowReloadMessage(false)
        // Clear localStorage if permission is default
        localStorage.removeItem('notificationPermissionGranted')
      } else if (permission === 'granted') {
        // Permission is granted - check if it was just granted in this session
        const justGranted = sessionStorage.getItem('notificationJustGranted') === 'true'
        const wasAlreadyGranted = localStorage.getItem('notificationPermissionGranted') === 'true'
        
        if (justGranted) {
          // Permission was just granted - show reload message
          setShowNotificationBox(true)
          setNotificationPermissionGranted(true)
          setShowReloadMessage(true)
        } else if (wasAlreadyGranted) {
          // Permission was already granted (from previous session) - hide the box
          setShowNotificationBox(false)
          setNotificationPermissionGranted(true)
          setShowReloadMessage(false)
        }
      } else if (permission === 'denied') {
        // Permission denied - show the box to ask user to enable
        setShowNotificationBox(true)
        setNotificationPermissionGranted(false)
        setShowReloadMessage(false)
        // Clear localStorage if permission is denied
        localStorage.removeItem('notificationPermissionGranted')
      }
    } else {
      // Browser doesn't support notifications
      setShowNotificationBox(false)
      localStorage.removeItem('notificationPermissionGranted')
    }
  }, [userType, showReloadMessage])

  // Check notification permission for interpreters
  useEffect(() => {
    const isInterpreter = userType === 'INTERPRETER'
    
    if (!isInterpreter) {
      setShowNotificationBox(false)
      return
    }

    // If reload message is showing, don't override (user just granted permission)
    if (showReloadMessage) {
      return
    }

    // If permission is granted, check if it was already granted before (on reload)
    if (typeof window !== 'undefined' && typeof window.Notification !== 'undefined') {
      const permission = window.Notification.permission
      const wasAlreadyGranted = localStorage.getItem('notificationPermissionGranted') === 'true'
      const justGranted = sessionStorage.getItem('notificationJustGranted') === 'true'
      
      if (permission === 'granted') {
        // If permission was just granted in this session, show reload message (don't hide)
        if (justGranted) {
          // Keep box visible with reload message
          return
        }
        // If permission was already granted (from previous session/reload), hide the box
        if (wasAlreadyGranted) {
          setShowNotificationBox(false)
          setNotificationPermissionGranted(true)
          setShowReloadMessage(false)
          return
        }
      }
    }
    
    // Otherwise, run the full check
    checkNotificationPermission()
  }, [checkNotificationPermission, userType, showReloadMessage])

  // Re-check permission when window regains focus (user might have changed it in browser settings)
  useEffect(() => {
    const handleFocus = () => {
      if (userType === 'INTERPRETER') {
        checkNotificationPermission()
      }
    }

    window.addEventListener('focus', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
    }
  }, [userType, checkNotificationPermission])

  const handleRequestNotificationPermission = async () => {
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') {
      toaster('error', 'Your browser does not support notifications')
      return
    }

    try {
      const permission = await window.Notification.requestPermission()
      
      if (permission === 'granted') {
        setNotificationPermissionGranted(true)
        setShowReloadMessage(true)
        setShowNotificationBox(true) // Keep box visible to show reload message
        // Don't set localStorage yet - set it after reload so we know to hide the box
        // For now, use sessionStorage to track that permission was just granted
        sessionStorage.setItem('notificationJustGranted', 'true')
        toaster('success', 'Notification permission granted! Please reload the page.')
      } else {
        toaster('error', 'Notification permission was denied. Please enable it in your browser settings.')
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      toaster('error', 'Failed to request notification permission')
    }
  }

  const handleReloadPage = () => {
    // Clear sessionStorage and set localStorage before reload
    sessionStorage.removeItem('notificationJustGranted')
    localStorage.setItem('notificationPermissionGranted', 'true')
    window.location.reload()
  }

  if (!showNotificationBox || userType !== 'INTERPRETER') {
    return null
  }

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .notification-box-mobile {
            max-width: 150px !important;
            min-width: 150px !important;
            padding: 10px !important;
          }
          .notification-box-mobile .notification-icon {
            display: none !important;
          }
          .notification-box-mobile .notification-text {
            display: none !important;
          }
          .notification-box-mobile .notification-content {
            justify-content: center !important;
            gap: 0 !important;
          }
        }
      `}</style>
      <div
        className='card notification-box-mobile'
        style={{
          backgroundColor: '#dc3545',
          color: '#ffffff',
          padding: '10px 20px',
          minWidth: '300px',
          maxWidth: '400px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        }}
      >
        {!showReloadMessage ? (
          <div className='d-flex align-items-center justify-content-between notification-content' style={{gap: '16px'}}>
            <div className='d-flex align-items-center'>
              <BellIcon className='fs-2 me-2 text-white notification-icon' />
              <h5 className='mb-0 fw-bold text-white notification-text'>Enable Notifications</h5>
            </div>
            <div>
              <button
                className='btn btn-light btn-sm'
                onClick={handleRequestNotificationPermission}
                style={{color: '#dc3545', fontWeight: '600'}}
              >
                Enable Notifications
              </button>
            </div>
          </div>
        ) : (
          <div className='d-flex align-items-center justify-content-between notification-content' style={{gap: '16px'}}>
            <div className='d-flex align-items-center'>
              <CheckCircleIcon className='fs-2 me-2 text-white notification-icon' />
              <h5 className='mb-0 fw-bold text-white notification-text'>Permission Granted!</h5>
            </div>
            <div>
              <button
                className='btn btn-light btn-sm'
                onClick={handleReloadPage}
                style={{color: '#dc3545', fontWeight: '600'}}
              >
                Reload Page
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

